import { useSearchParams } from 'react-router-dom';
import { makeStyles, tokens, Text } from '@fluentui/react-components';
import { useAppStore } from '@/stores/appStore';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AppOverviewHeader } from '@/components/shared/AppOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import {
  ConnectionsFooterActions,
  ConnectionsView,
} from '@/components/ConnectionList/ConnectionsView';
import { useConnectionsEditor } from '@/components/ConnectionList/useConnectionsEditor';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  footer: {
    paddingTop: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * ConnectionsPage — full-page wrapper around `ConnectionsView`. Mirrors
 * `ConnectionsDialog` so the route and the modal stay behaviorally identical.
 *
 * Renders the standard app shell (`AppOverviewHeader` + `PageSection`) for
 * consistency with the other app-scoped pages.
 */
export const ConnectionsPage = () => {
  const styles = useStyles();
  const [searchParams] = useSearchParams();
  const { app, appName, locationName } = useCurrentApp();
  const { projectName: routeProjectName } = useCurrentProject();
  const selectedApp = useAppStore((s) => s.selectedApp);
  // Connections editor was historically wired through the store-based
  // selection. Keep that wiring intact so we don't perturb the editor
  // subsystem, but power the page chrome from URL-derived context.
  // The URL is the single source of truth for the project so a stale
  // Zustand selection can't trick the editor into reading/writing the
  // wrong project. `?project=` is honored only as an explicit override.
  const editorProjectName = searchParams.get('project') ?? routeProjectName ?? '';
  const selectedMatchesRoute =
    !!selectedApp?.name && !!appName && selectedApp.name.toLowerCase() === appName.toLowerCase();
  const routeMatchedApp = selectedMatchesRoute ? selectedApp : undefined;
  const editorAppName = appName ?? routeMatchedApp?.name ?? '';
  const editorAppId = app?.properties.appResourceId ?? routeMatchedApp?.properties.appResourceId ?? '';

  useDocumentTitle(['Connections', editorAppName, editorProjectName]);

  const { data: workflows } = useAppWorkflows(app ?? null);
  const state = useConnectionsEditor({
    projectName: editorProjectName,
    appName: editorAppName,
    appId: editorAppId,
    enabled: !!editorAppName && !!editorAppId,
  });

  const headerName = editorAppName || appName || '';

  if (!headerName) {
    return (
      <div className={styles.page}>
        <PageSection title="Connections">
          <div className={styles.empty}>
            <Text>Select an application to view connections.</Text>
          </div>
        </PageSection>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AppOverviewHeader
        name={headerName}
        app={app}
        region={locationName}
        workflowCount={workflows?.length ?? 0}
      />
      <PageSection title="Connections">
        <div className={styles.body}>
          <ConnectionsView state={state} />
        </div>
        {state.selectedTab === 'raw' && (
          <div className={styles.footer}>
            <ConnectionsFooterActions state={state} />
          </div>
        )}
      </PageSection>
    </div>
  );
};

ConnectionsPage.displayName = 'ConnectionsPage';
